import debounce from 'just-debounce';

/**
 * @class
 * @classdesc This component handles opening the drawer filter menu (usually used on mobile).
 *
 * @extends Core.Component
 */
export default class DrawerFilters extends Core.Component {
  constructor(el, context) {
    super(el, context);

    this.drawerFiltersEl = el.querySelector('[data-drawer-filters]');
    this.closeButton = el.querySelector('[data-drawer-filters-close]');
    this.clearButton = el.querySelector('[data-drawer-filters-clear]');
    this.filterForm = el.querySelector('[data-filter-form]');
    this.filters = el.querySelector('[data-filter-disclosures]');
    this.emptyFilters = el.querySelector('[data-filters-empty]');
    this.summaryElements = this.filters.querySelectorAll('[data-filter-summary]');
    this.viewResultsButton = el.querySelector('[data-drawer-view-results] button');
    this.searchInput = el.querySelector('[data-search-input]');

    this.summaryClickEvents = {};
    this.contentCloseEvents = {};
    this.summaryAnimations = {};

    this.formChange = this._formChange.bind(this);
  }

  onMount() {
    this.drawerModal = Core.Components.getComponent('drawer-modal');

    if (!this.drawerModal) return;

    this.drawerFlyout = this.drawerModal.createFlyout(this.drawerFiltersEl);
  }

  onDismount() {
    super.onDismount();

    if (this.drawerFlyout) {
      this.drawerFlyout.then(drawerFlyout => drawerFlyout.destroy());
    }
  }

  /**
   * Creates a flyout by moving the content into a drawer and then opens the navigation drawer
   *
   * @param {HTMLElement} trigger The trigger that opened this drawer.
   */
  async open(trigger) {
    if (!this.drawerFlyout) return;

    const drawerFlyout = await this.drawerFlyout;

    // We only register the events once the flyout is opening for the first time, because when
    // we create the flyout, the element is moved and thus re-mounted. If we called this in
    // onMount or the constructor, the events would be created twice.
    this.events.register(this.filterForm, 'input', debounce(e => this._formChange(e), 500));
    this.events.register(this.closeButton, 'click', () => this.drawerModal.closeFlyout(drawerFlyout));
    this.events.register(this.viewResultsButton, 'click', e => {
      e.preventDefault();
      this.drawerModal.closeFlyout(drawerFlyout);
    });
    this.events.register(window, 'keyup', e => { if (e.key === 'Escape') this.closeAllDisclosures(); });
    this.summaryElements.forEach(summaryElement => this._initSummary(summaryElement));

    this.drawerFiltersEl.dataset.ready = 'true';

    // Close all disclosures before opening again.
    this.closeAllDisclosures();
    this.drawerModal.openFlyout(drawerFlyout, trigger);
  }

  /**
   * Main entrypoint to update all of the components of the drawer filters with any changes
   * based on the newDom.
   *
   * @param {HTMLElement} newDom - The new DOM which should include a new version of the drawer
   * filters html.
   */
  updateFilters(newDom) {
    const newDrawerDom = newDom.querySelector('[data-drawer-filters]');

    this.clearButton.href = newDom.querySelector('[data-drawer-filters-clear]').href;
    this.viewResultsButton.innerHTML = newDom.querySelector('[data-drawer-view-results] button').innerHTML;

    this.emptyFilters.classList = newDrawerDom.querySelector('[data-filters-empty]').classList;

    this._updateDisclosures(newDrawerDom);
  }

  /**
   * Closes all disclosures that were opened within the flyout.
   */
  closeAllDisclosures() {
    this.summaryElements = this.filters.querySelectorAll('[data-filter-summary]');
    this.summaryElements.forEach(summaryElement => {
      const { parentElement } = summaryElement;
      const { key } = parentElement.dataset;
      if (this.summaryAnimations[key]) {
        this.summaryAnimations[key].animateTo('closed').then(() => {
          parentElement.open = false;
        });
      }
    });
  }

  /**
   * Triggered when a summary element is clicked and should open the content of the details
   * with another drawer.
   *
   * @param {Summary} summaryElement The summary element that has received a click event
   * @private
   */
  _initSummary(summaryElement) {
    const { parentElement } = summaryElement;
    const { key } = parentElement.dataset;
    const contentElement = summaryElement.nextElementSibling;
    const backButton = contentElement.querySelector('[data-filter-back-button]');

    this.summaryAnimations[key] = Core.Animations.transition({ el: contentElement, state: 'closed' });

    this.summaryClickEvents[key] = this.events.register(summaryElement, 'click', () => {
      this.summaryAnimations[key].animateTo('open').then(() => {
        Core.A11y.trapFocus(contentElement);
      });
    });

    this.contentCloseEvents[key] = this.events.register(backButton, 'click', async e => {
      if (!this.drawerFlyout) return;

      const drawerFlyout = await this.drawerFlyout;

      e.preventDefault();
      this.summaryAnimations[key]
        .animateTo('closed')
        .then(() => {
          parentElement.open = false;
          drawerFlyout.trapFocus();
          summaryElement.focus();
        });
    });
  }

  /**
   * Removes all of the actions and animations that are stored for the summary element click event.
   *
   * @param {Summary} summaryElement The element to be removed.
   * @private
   */
  _removeSummaryActions(summaryElement) {
    const { key } = summaryElement.parentElement.dataset;

    delete this.summaryAnimations[key];

    if (this.summaryClickEvents[key]) {
      this.events.unregister(this.summaryClickEvents[key]);
    }

    if (this.contentCloseEvents[key]) {
      this.events.unregister(this.contentCloseEvents[key]);
    }
  }

  /**
   * Triggered when one of the filters are updated in the form.
   * Dispatches an event called `filter:update` which passes along `searchParams` from the
   * filters form.
   *
   * @param {Event} e - The event sent by the form being updated.
   * @private
   */
  _formChange(e) {
    e.preventDefault();

    const currentTarget = e.target;

    const formData = new FormData(currentTarget.closest('form'));
    const searchParams = new URLSearchParams(formData).toString();

    // Fire off event to submit the new params to the parent section
    const updateEvent = new CustomEvent('filter:update', { detail: { searchParams } });
    this.el.dispatchEvent(updateEvent);
  }

  /**
   * Updates all of the filter disclosures with a newDom.
   * Will only update disclosures if focus is not currently within the disclosure so the user does
   * not lose focus.
   *
   * @param {HTMLElement} newDom - The new DOM which should include a new version of the faceted
   * filters html.
   * @private
   */
  _updateDisclosures(newDom) {
    const currentDisclosure = document.activeElement && document.activeElement.closest('[data-filter-disclosure]');
    const existingDisclosures = this.filters.querySelectorAll('[data-filter-disclosure]');
    const filterDisclosures = newDom.querySelectorAll('[data-filter-disclosure]');

    if (this.searchInput) {
      this.searchInput.value = newDom.querySelector('[data-search-input]').value;
    }

    // Remove any disclosures that don't exist anymore in the new Dom
    existingDisclosures.forEach(disclosure => {
      if (!newDom.querySelector(`[data-filter-disclosure][data-key="${disclosure.dataset.key}"]`)) {
        this._removeSummaryActions(disclosure.querySelector('[data-filter-summary]'));
        disclosure.remove();
      }
    });

    // Update the existing Dom with the updated and new disclosures from the new Dom
    filterDisclosures.forEach(disclosure => {
      const oldDisclosure = this.filterForm.querySelector(`[data-filter-disclosure][data-key="${disclosure.dataset.key}"]`);

      if (!oldDisclosure) {
        this.filters.appendChild(disclosure);
        this._initSummary(disclosure.querySelector('[data-filter-summary]'));
      } else {
        if (!currentDisclosure || disclosure.dataset.key !== currentDisclosure.dataset.key) {
          const filterContent = oldDisclosure.querySelector('[data-filter-form-items]');
          filterContent.innerHTML = disclosure.querySelector('[data-filter-form-items]').innerHTML;
        }

        const filterSummary = oldDisclosure.querySelector('[data-filter-summary]');
        filterSummary.innerHTML = disclosure.querySelector('[data-filter-summary]').innerHTML;

        const filterHeader = oldDisclosure.querySelector('[data-filter-heading]');
        filterHeader.innerHTML = disclosure.querySelector('[data-filter-heading]').innerHTML;

        const resetButton = oldDisclosure.querySelector('[data-filter-reset]');
        resetButton.href = disclosure.querySelector('[data-filter-reset]').href;
      }
    });
  }
}
